import {
  Banknote,
  Building2,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  Clock,
  Home,
  KeyRound,
  MapPin,
  PiggyBank,
  UserRound,
} from "lucide-react";
import { type ReactNode, useId, useState } from "react";

import { cn } from "../../../../lib/utils";
import {
  useTargetEdit,
  StaleTargetError,
  type TargetEditPatch,
  type UseTargetEditOptions,
} from "../../../../lib/profile/use-target-edit";
import type { ProfileRowId } from "../../../../lib/readiness/factor-routing";
import {
  getTargetLocationError,
  resolveTargetLocation,
  type TargetLocation,
} from "../../../../lib/target-location";
import { targetSaveErrorMessage } from "../../../../lib/target-unresolvable";
import { extractUSStateCodeFromLocation } from "../../../../lib/us-location";
import { Button } from "../../../ui/button";
import { Dialog, DialogContent } from "../../../ui/dialog";
import { Input } from "../../../ui/input";
import { Select } from "../../../ui/select";
import { AreaSearch } from "../../../onboarding/area-search";

const NUMERIC_PATCH_KEYS = new Set<keyof TargetEditPatch>([
  "preferred_term_mo",
  "units",
]);

export interface TargetGoalRowOption {
  value: string;
  label: string;
}

export interface TargetGoalRowProps {
  rowId: Extract<
    ProfileRowId,
    | "target_price"
    | "target_area"
    | "property_type_occupancy"
    | "occupancy"
    | "loan_product"
    | "loan_term"
    | "buyer_type"
    | "timeline"
    | "down_payment_plan"
  >;
  label: string;
  // Pre-formatted display string for the collapsed row (e.g., "$540k").
  value: string;
  patchKey: keyof TargetEditPatch;
  inputType: "currency" | "text" | "select" | "target-location";
  options?: TargetGoalRowOption[];
  // Current raw value used to (a) seed the editor's draft state, (b) detect
  // no-op edits to skip the network call.
  currentTargetValue: number | string | null;
  warning?: ReactNode;
  readOnlyReason?: ReactNode;
  readOnlyAction?: {
    href: string;
    label: string;
  };
  onAddProperty?: () => void;
  midpointAvailable?: boolean;
  midpointRevision?: number;
  // Mutation deps (constructed once at parent level — see TargetCard / GoalsCard).
  opts: UseTargetEditOptions;
}

// TargetGoalRow renders one editable Profile Goals row backed by
// PUT /api/readiness/target. Each row owns its own useTargetEdit hook so
// per-row error/loading state stays isolated; the opts object passed in
// must be stable across renders (memoize at the parent or build once in a
// caller-owned closure).
//
// TODO(SILL-187 follow-up): accept idPrefix prop if ProfileView is ever
// embedded twice on a page; today the anchor id is page-singleton.
export function TargetGoalRow({
  rowId,
  label,
  value,
  patchKey,
  inputType,
  options,
  currentTargetValue,
  warning,
  readOnlyReason,
  readOnlyAction,
  onAddProperty,
  midpointAvailable,
  midpointRevision,
  opts,
}: TargetGoalRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  // The label of the most recent location save, so a `target_unresolvable`
  // refusal can name what the buyer typed (the 422 body never echoes it).
  const [attemptedLabel, setAttemptedLabel] = useState<string | null>(null);
  const [pendingConfirmation, setPendingConfirmation] =
    useState<TargetEditPatch | null>(null);
  const initialDraft =
    currentTargetValue == null ? "" : String(currentTargetValue);
  const [selectDraftState, setSelectDraftState] = useState({
    base: initialDraft,
    value: initialDraft,
  });
  const selectDraft =
    selectDraftState.base === initialDraft
      ? selectDraftState.value
      : initialDraft;
  const setSelectDraft = (value: string) => {
    setSelectDraftState({ base: initialDraft, value });
  };
  const editTarget = useTargetEdit(opts);
  const editorId = useId();
  const error = editTarget.error instanceof StaleTargetError
    ? "Your target or shopping range changed. Review the latest values before saving again."
    : editTarget.error
    ? targetSaveErrorMessage(
        editTarget.error,
        attemptedLabel,
        "We couldn't confirm the save. Review the latest target before trying again.",
      )
    : validationError
      ? validationError
      : undefined;

  // The editor is uncontrolled (defaultValue on mount; we read from
  // e.currentTarget.value on blur). For a blur-to-commit form this is
  // simpler than tracking React state — the DOM is the source of truth
  // until the user blurs, which is exactly when we read it.
  const handleCommit = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.currentTarget.value;
    commitValue(raw);
  };

  const requiresReadinessConfirmation =
    rowId === "target_price" || rowId === "target_area";
  // "Update target city or zip?" reads badly lowercased; the area row's
  // confirmation keeps talking about the target area.
  const confirmSubject =
    rowId === "target_area" ? "target area" : label.toLowerCase();

  const mutatePatch = (patch: TargetEditPatch) => {
    setAttemptedLabel(patch.label ?? null);
    editTarget.mutate(patch).catch(() => {
      // Surfaced via editTarget.error; the catch is just to suppress the
      // unhandled-rejection warning on the onBlur path.
    });
  };

  const requestMutation = (patch: TargetEditPatch) => {
    setValidationError(null);
    if (requiresReadinessConfirmation) {
      setPendingConfirmation(patch);
      return;
    }

    mutatePatch(patch);
  };

  const handleConfirmMutation = () => {
    const patch = pendingConfirmation;
    if (!patch) return;

    setPendingConfirmation(null);
    mutatePatch(patch);
  };

  // `picked` is the suggestion the buyer chose in the area picker, when they
  // chose one: its place_id rides the save so the server resolves that exact
  // place. A typed or blurred value passes none.
  const commitValue = (raw: string, picked?: TargetLocation) => {
    // A picked suggestion carries a place_id, and a new place_id re-resolves
    // (decision 580) even when Google's label equals the stored one - a legacy
    // "Seattle, WA" confirmed through the picker gets its first geography.
    if (raw === initialDraft && !picked?.place_id) {
      // No-op edit — skip the network round-trip entirely.
      setValidationError(null);
      return;
    }
    let patch: TargetEditPatch;
    if (inputType === "target-location") {
      // The server decides whether this resolves (422 target_unresolvable):
      // the patch carries what the buyer chose, never an FE-side judgement.
      // Null here is only an empty value or a street address.
      const targetLocation = resolveTargetLocation(raw, picked);
      if (!targetLocation) {
        setValidationError(getTargetLocationError(raw));
        return;
      }
      // `location_code` keeps STATE semantics for pricing (decision 575), or
      // the ZIP itself for a typed ZIP; the server's `target_zip` is derived
      // from label + place_id, not from this. ALWAYS set: the patch merges onto
      // a draft that carries the prior code, and a prior ZIP would otherwise
      // place this new text at the old ZIP (decision 566: never guess). "" when
      // the text names neither a ZIP nor a state.
      const locationCode =
        targetLocation.kind === "zip"
          ? targetLocation.label
          : (extractUSStateCodeFromLocation(targetLocation.label) ?? "");
      patch = {
        label: targetLocation.label,
        ...(targetLocation.place_id
          ? { place_id: targetLocation.place_id }
          : {}),
        location_code: locationCode,
      };
    } else {
      const next: TargetEditPatch[typeof patchKey] =
        inputType === "currency" ? Number(raw) : raw;
      patch = {
        [patchKey]: next,
        ...(patchKey === "price" ? { price_intent: "buyer_selected" } : {}),
      } as TargetEditPatch;
      if (patchKey === "location_code" && typeof next === "string") {
        patch.label = next;
      }
    }
    requestMutation(patch);
  };

  const handleSelectCommit = (raw: string) => {
    if (raw === initialDraft) {
      setSelectDraft(raw);
      return;
    }
    setSelectDraft(raw);
    // Backend types vary per patchKey; the Select element only emits
    // strings, so numeric-typed fields (e.g. preferred_term_mo) need
    // coercion before serialization or the JSON unmarshal will reject.
    const next = NUMERIC_PATCH_KEYS.has(patchKey) ? Number(raw) : raw;
    editTarget
      .mutate({
        [patchKey]: next as TargetEditPatch[typeof patchKey],
      } as TargetEditPatch)
      .catch(() => {
        setSelectDraft(initialDraft);
      });
  };

  return (
    <>
      <article
        id={`profile-row-${rowId}`}
        data-state={readOnlyReason ? "readonly" : expanded ? "open" : "closed"}
        className="overflow-hidden p-0 transition-colors hover:bg-accent/35"
      >
        {readOnlyReason ? (
          <div className="flex min-h-[4.75rem] w-full items-center justify-between gap-3 px-4 py-3 text-left sm:px-5">
            <span className="flex min-w-0 items-center gap-3">
              <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
                <GoalRowGlyph rowId={rowId} />
              </span>
              <span className="block min-w-0">
                <span className="block truncate text-base font-semibold text-foreground">
                  {label}
                </span>
                <span className="mt-1 block text-xs font-medium leading-5 text-muted-foreground">
                  {readOnlyReason}
                </span>
              </span>
            </span>
            <span className="flex min-w-0 shrink-0 flex-col items-end gap-2">
              <span className="block min-w-0 truncate text-right text-sm font-semibold text-foreground">
                {value}
              </span>
              {readOnlyAction ? (
                <Button
                  asChild
                  className="min-h-8 rounded-pill px-3 text-xs"
                  size="sm"
                  variant="outline"
                >
                  <a href={readOnlyAction.href}>{readOnlyAction.label}</a>
                </Button>
              ) : null}
            </span>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex min-h-[4.75rem] w-full items-center justify-between gap-3 px-4 py-3 text-left sm:px-5"
            aria-label={label}
            aria-expanded={expanded}
            data-slot="interactive-control"
          >
            <span className="flex min-w-0 items-center gap-3">
              <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
                <GoalRowGlyph rowId={rowId} />
              </span>
              <span className="block min-w-0 truncate text-base font-semibold text-foreground">
                {label}
              </span>
            </span>
            <span className="flex min-w-0 shrink-0 items-center justify-end gap-3">
              <span className="block min-w-0 truncate text-right text-sm font-semibold text-foreground">
                {value}
              </span>
              {expanded ? (
                <ChevronUp aria-hidden="true" className="h-4 w-4" />
              ) : (
                <ChevronDown aria-hidden="true" className="h-4 w-4" />
              )}
            </span>
          </button>
        )}
        {!readOnlyReason && expanded && (
          <div className="border-t border-border-soft bg-background/60 px-4 pb-4 pt-4 sm:px-5">
            <div className="max-w-xl">
              {inputType === "select" && options ? (
                <Select
                  disabled={editTarget.isPending}
                  error={error}
                  items={options}
                  label={label}
                  onValueChange={handleSelectCommit}
                  placeholder="Select..."
                  value={selectDraft}
                />
              ) : inputType === "target-location" ? (
                <>
                  <AreaSearch
                    onChange={() => setValidationError(null)}
                    onCommit={commitValue}
                    value={initialDraft}
                  />
                  {error ? (
                    <p
                      className="mt-2 text-sm font-medium text-destructive"
                      role="alert"
                    >
                      {error}
                    </p>
                  ) : null}
                </>
              ) : (
                <Input
                  key={initialDraft}
                  type={inputType === "currency" ? "number" : "text"}
                  className={cn(
                    inputType === "currency" && "font-bold tabular-nums",
                  )}
                  defaultValue={initialDraft}
                  aria-label={label}
                  disabled={editTarget.isPending}
                  error={error}
                  id={editorId}
                  inputMode={inputType === "currency" ? "numeric" : undefined}
                  label={label}
                  min={inputType === "currency" ? 0 : undefined}
                  onBlur={handleCommit}
                  startAdornment={inputType === "currency" ? "$" : undefined}
                />
              )}
              {rowId === "target_price" && currentTargetValue != null ? (
                <Button
                  className="mt-3 mr-2"
                  variant="outline"
                  onClick={() =>
                    requestMutation({
                      price: Number(currentTargetValue),
                      price_intent: "buyer_selected",
                    })
                  }
                >
                  Save as my target price
                </Button>
              ) : null}
              {rowId === "target_price" && midpointAvailable ? (
                <Button
                  className="mt-3"
                  variant="outline"
                  onClick={() =>
                    requestMutation({ price_intent: "range_midpoint", shopping_range_revision: midpointRevision })
                  }
                >
                  Use range midpoint
                </Button>
              ) : null}
              {inputType === "target-location" && onAddProperty ? (
                <div className="mt-3 surface-card px-4 py-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        Have a specific address?
                      </p>
                      <p className="mt-1 text-sm leading-6 text-muted-foreground">
                        Add it as a saved property instead of using the broad
                        area target.
                      </p>
                    </div>
                    <Button
                      className="shrink-0 rounded-pill"
                      onClick={onAddProperty}
                      size="sm"
                      variant="outline"
                    >
                      <Home aria-hidden="true" className="h-4 w-4" />
                      Add a property
                    </Button>
                  </div>
                </div>
              ) : null}
              {warning ? (
                <div
                  role="note"
                  className="mt-3 rounded-2xl border border-warning/25 bg-warning/10 px-4 py-3 text-sm leading-6 text-warning-foreground"
                >
                  <p className="font-semibold">Heads up</p>
                  <p className="text-current/85">{warning}</p>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </article>
      <Dialog
        open={pendingConfirmation !== null}
        onOpenChange={(open) => !open && setPendingConfirmation(null)}
      >
        <DialogContent
          title={`Update ${confirmSubject}?`}
          description="This changes the readiness target your score and guidance are measured against."
        >
          <div className="space-y-4">
            <p className="text-sm leading-6 text-muted-foreground">
              Sill will recalculate buying power, payment guidance, and
              readiness factors using the new {confirmSubject}.
            </p>
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button
                disabled={editTarget.isPending}
                onClick={() => setPendingConfirmation(null)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                loading={editTarget.isPending}
                onClick={handleConfirmMutation}
              >
                Update readiness target
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function GoalRowGlyph({ rowId }: { rowId: TargetGoalRowProps["rowId"] }) {
  const props = {
    "aria-hidden": true,
    className: "size-4",
    strokeWidth: 2,
  } as const;

  switch (rowId) {
    case "target_price":
      return <Home {...props} />;
    case "target_area":
      return <MapPin {...props} />;
    case "property_type_occupancy":
      return <Building2 {...props} />;
    case "occupancy":
      return <KeyRound {...props} />;
    case "loan_product":
      return <Banknote {...props} />;
    case "loan_term":
      return <Clock {...props} />;
    case "buyer_type":
      return <UserRound {...props} />;
    case "timeline":
      return <CalendarClock {...props} />;
    case "down_payment_plan":
      return <PiggyBank {...props} />;
  }
}
